/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/FAQSection/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import styles from "./FAQSection.module.css";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import SearchBar from "../SearchBar/index.jsx";

import { ReactComponent as ChevronDownIcon } from "../../assets/icons/FAQSection/ChevronDownIcon.svg";
import { ReactComponent as ChevronUpIcon } from "../../assets/icons/FAQSection/ChevronUpIcon.svg";

import { ReactComponent as ProfileIcon } from "../../assets/icons/FAQSection/ProfileIcon.svg";
import { ReactComponent as SecurityIcon } from "../../assets/icons/FAQSection/SecurityIcon.svg";
import { ReactComponent as WorkspaceIcon } from "../../assets/icons/FAQSection/WorkspaceIcon.svg";
import { ReactComponent as EventsIcon } from "../../assets/icons/FAQSection/EventsIcon.svg";
import { ReactComponent as RequirementsIcon } from "../../assets/icons/FAQSection/RequirementsIcon.svg";
import { ReactComponent as SupportIcon } from "../../assets/icons/FAQSection/SupportIcon.svg";

const FAQSection = () => {
  const { t } = useTranslation();
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");

  // Generate FAQ data from translation files
  const generateFAQData = () => {
    const sections = [
      {
        id: "section1",
        title: t("components.FAQSection.section1"),
        icon: <ProfileIcon />,
      },
      {
        id: "section2",
        title: t("components.FAQSection.section2"),
        icon: <WorkspaceIcon />,
      },
      {
        id: "section3",
        title: t("components.FAQSection.section3"),
        icon: <EventsIcon />,
      },
      {
        id: "section4",
        title: t("components.FAQSection.section4"),
        icon: <RequirementsIcon />,
      },
      {
        id: "section5",
        title: t("components.FAQSection.section5"),
        icon: <SupportIcon style={{ width: "18px", height: "18px" }} />,
      },
      {
        id: "section6",
        title: t("components.FAQSection.section6"),
        icon: <SecurityIcon />,
      },
    ];

    return sections
      .map((section) => {
        const items = [];
        let questionNumber = 1;

        // Check for questions and answers for this section
        while (true) {
          const questionKey = `${section.id}Question${questionNumber}`;
          const answerKey = `${section.id}Answer${questionNumber}`;

          try {
            const question = t(`components.FAQSection.${questionKey}`);
            const answer = t(`components.FAQSection.${answerKey}`);

            // If the translation key doesn't exist, it will return the key itself
            // We check if it's different from the key to know if it exists
            if (
              question !== `components.FAQSection.${questionKey}` &&
              answer !== `components.FAQSection.${answerKey}`
            ) {
              items.push({
                id: `${section.id}-${questionNumber}`,
                question,
                answer,
              });
              questionNumber++;
            } else {
              break;
            }
          } catch (error) {
            break;
          }
        }

        return {
          ...section,
          items,
        };
      })
      .filter((section) => section.items.length > 0); // Only include sections with items
  };

  const faqData = generateFAQData();

  const toggleItem = (id) => {
    const newExpandedItems = new Set(expandedItems);
    if (newExpandedItems.has(id)) {
      newExpandedItems.delete(id);
    } else {
      newExpandedItems.add(id);
    }
    setExpandedItems(newExpandedItems);
  };

  const toggleCategory = (categoryId) => {
    const newCollapsedCategories = new Set(collapsedCategories);
    if (newCollapsedCategories.has(categoryId)) {
      newCollapsedCategories.delete(categoryId);
    } else {
      newCollapsedCategories.add(categoryId);
    }
    setCollapsedCategories(newCollapsedCategories);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  // Filter FAQ items based on search query
  const filteredFaqData = faqData
    .map((category) => {
      // Check if category title matches search query
      const categoryMatches = category.title
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

      // Filter items within the category
      const filteredItems = category.items.filter(
        (item) =>
          item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.answer.toLowerCase().includes(searchQuery.toLowerCase()),
      );

      // If category title matches, show all items in that category
      // Otherwise, only show items that match the search query
      const itemsToShow = categoryMatches ? category.items : filteredItems;

      return {
        ...category,
        items: itemsToShow,
      };
    })
    .filter((category) => category.items.length > 0);

  return (
    <div className={styles.faqContainer}>
      <div className={styles.faqHeader}>
        <h2 className={styles.faqTitle}>{t("components.FAQSection.title")}</h2>
      </div>

      {/* Search Bar */}
      <SearchBar
        placeholder={t("components.FAQSection.searchText")}
        value={searchQuery}
        onChange={handleSearch}
        className={styles.searchSection}
      />

      <div className={styles.faqList}>
        {filteredFaqData.length === 0 ? (
          <div className={styles.noResults}>
            <p>{t("components.FAQSection.noResults")}</p>
          </div>
        ) : (
          filteredFaqData.map((category) => (
            <div key={category.id} className={styles.faqCategory}>
              <div
                className={styles.categoryHeader}
                onClick={() => toggleCategory(category.id)}
              >
                <div className={styles.categoryIcon}>{category.icon}</div>
                <h3 className={styles.categoryTitle}>{category.title}</h3>
                <div className={styles.categoryChevron}>
                  {collapsedCategories.has(category.id) ? (
                    <ChevronDownIcon />
                  ) : (
                    <ChevronUpIcon />
                  )}
                </div>
              </div>
              {!collapsedCategories.has(category.id) && (
                <div className={styles.categoryItems}>
                  {category.items.map((item) => (
                    <div key={item.id} className={styles.faqItem}>
                      <div
                        className={styles.faqQuestion}
                        onClick={() => toggleItem(item.id)}
                      >
                        <h4 className={styles.questionText}>{item.question}</h4>
                        <div className={styles.chevronIcon}>
                          {expandedItems.has(item.id) ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )}
                        </div>
                      </div>
                      {expandedItems.has(item.id) && (
                        <div className={styles.faqAnswer}>
                          <p>{item.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FAQSection;
